import {
  init as sdkInit,
  miniApp,
  themeParams,
  backButton,
  hapticFeedback,
  initData,
  openTelegramLink as sdkOpenTelegramLink,
  type LaunchParams,
  retrieveLaunchParams,
} from '@telegram-apps/sdk-react';

let sdkReady = false;

export function initTelegramSdk(): void {
  if (sdkReady) return;
  try {
    sdkInit();
    if (miniApp.mountSync.isAvailable()) miniApp.mountSync();
    if (themeParams.mountSync.isAvailable()) themeParams.mountSync();
    initData.restore();
    sdkReady = true;
  } catch {
    sdkReady = false;
  }
}

export function isTelegramMiniApp(): boolean {
  return sdkReady;
}

export function getLaunchParams(): LaunchParams | null {
  if (!sdkReady) return null;
  try {
    return retrieveLaunchParams();
  } catch {
    return null;
  }
}

export function getStartParam(): string | undefined {
  if (!sdkReady) return undefined;
  try {
    const lp = retrieveLaunchParams();
    return (lp as Record<string, unknown>).startParam as string | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Parse the start_param from Telegram deep link.
 * `lot_42` -> { type: 'lot', id: '42' }
 * `ref_0QAbc...` -> { type: 'ref', id: '0QAbc...' }
 */
export function parseStartParam(param?: string): { type: string; id: string } | null {
  if (!param) return null;
  if (param.startsWith('ref_')) {
    return { type: 'ref', id: param.slice(4) };
  }
  const match = param.match(/^(\w+)_(\d+)$/);
  if (!match) return null;
  return { type: match[1], id: match[2] };
}

export function getTelegramUserId(): number | undefined {
  if (!sdkReady) return undefined;
  try {
    const user = initData.user();
    return user?.id;
  } catch {
    return undefined;
  }
}

export function getColorScheme(): 'light' | 'dark' | undefined {
  if (!sdkReady) return undefined;
  try {
    return miniApp.isDark() ? 'dark' : 'light';
  } catch {
    return undefined;
  }
}

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  if (!sdkReady) return;
  try {
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred(type);
    }
  } catch { /* noop outside Telegram */ }
}

export function hapticSuccess() {
  if (!sdkReady) return;
  try {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success');
    }
  } catch { /* noop */ }
}

export function hapticError() {
  if (!sdkReady) return;
  try {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('error');
    }
  } catch { /* noop */ }
}

export function showBackButton(onBack: () => void): () => void {
  if (!sdkReady) return () => {};
  try {
    if (backButton.mount.isAvailable()) backButton.mount();
    if (backButton.show.isAvailable()) backButton.show();
    if (backButton.onClick.isAvailable()) backButton.onClick(onBack);
    return () => {
      try {
        if (backButton.offClick.isAvailable()) backButton.offClick(onBack);
        if (backButton.hide.isAvailable()) backButton.hide();
      } catch { /* noop */ }
    };
  } catch {
    return () => {};
  }
}

export function openTelegramLink(url: string): void {
  if (!sdkReady) return;
  try {
    if (sdkOpenTelegramLink.isAvailable()) {
      sdkOpenTelegramLink(url);
    }
  } catch { /* noop */ }
}

export { backButton, themeParams, miniApp } from '@telegram-apps/sdk-react';
